import { forwardRef, useEffect, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { Link } from 'react-router';
import type { ChipForm, StatusLabel } from '@wasity/shared';
import { useI18n, formatDateTime } from '@/i18n';
import { dateOnly, slotLabel } from '@/lib/format';
import { errorCodeOf, specificErrorText } from '@/lib/errors';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

// ── Dates ───────────────────────────────────────────────────────────────────────
/**
 * A localized date is prose, not a number. `.num` forces `direction: ltr` on the
 * whole string, so an Arabic date that starts with a digit («10 أكتوبر 2026») takes
 * the LTR base direction and the month and year swap («10 2026 أكتوبر»). Render every
 * `formatDateTime` / `dateOnly` / `slotLabel` string through this component instead:
 * it keeps the tabular figures and the one-line rule, and leaves the direction alone.
 */
export function DateText({ at, kind = 'dateTime', className, empty = '—', wrap }: { at: string | Date | null | undefined; kind?: 'dateTime' | 'date' | 'slot'; className?: string; empty?: string; wrap?: boolean }) {
  const { locale } = useI18n();
  const text = at == null || at === '' ? empty : kind === 'date' ? dateOnly(at, locale) : kind === 'slot' ? slotLabel(at, locale) : formatDateTime(at, locale);
  return <span className={cx('tabular-nums', !wrap && 'whitespace-nowrap', className)}>{text}</span>;
}

// ── Buttons ─────────────────────────────────────────────────────────────────────
type Variant = 'primary' | 'accent' | 'secondary' | 'text' | 'danger';
const buttonBase = 'inline-flex items-center justify-center gap-2 min-h-11 px-5 rounded-lg font-medium text-[15px] transition-colors duration-150 disabled:opacity-50 disabled:pointer-events-none cursor-pointer';
// A disabled filled button sits on mint with muted text instead of a washed-out ink (same rule as the app); outlined ones fade.
const filledDisabled = 'disabled:opacity-100 disabled:bg-bg-2 disabled:text-muted';
const variants: Record<Variant, string> = {
  primary: `bg-primary text-primary-fg hover:bg-accent hover:text-accent-fg ${filledDisabled}`,
  accent: `bg-accent text-accent-fg hover:bg-primary hover:text-primary-fg ${filledDisabled}`,
  secondary: 'border border-input-line text-fg hover:bg-bg-2',
  text: 'px-2 min-h-9 text-accent hover:text-fg',
  danger: 'border border-error text-error hover:bg-error-tint',
};
export function Button({ variant = 'primary', loading, className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; loading?: boolean }) {
  return (
    <button className={cx(buttonBase, variants[variant], className)} disabled={loading || rest.disabled} {...rest}>
      {loading ? <span className="inline-block size-4 rounded-full border-2 border-current border-e-transparent animate-spin" aria-hidden /> : null}
      {children}
    </button>
  );
}

// ── Inputs ──────────────────────────────────────────────────────────────────────
const fieldBase = 'w-full bg-transparent border border-input-line rounded-sm px-3.5 py-3 text-[15px] text-fg placeholder:text-muted transition-colors focus:border-accent';
export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(function Input({ className, invalid, ...rest }, ref) {
  return <input ref={ref} className={cx(fieldBase, invalid && 'border-error', className)} {...rest} />;
});
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={cx(fieldBase, 'min-h-28', className)} {...rest} />;
});
export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cx(fieldBase, 'appearance-none', className)} {...rest}>
      {children}
    </select>
  );
}
export function Field({ label, hint, error, action, children, htmlFor }: { label: string; hint?: string; error?: string; action?: ReactNode; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={htmlFor} className="text-sm font-medium text-fg">{label}</label>
        {action}
      </div>
      {children}
      {error ? <p className="text-[13px] text-error">{error}</p> : hint ? <p className="text-[13px] text-muted">{hint}</p> : null}
    </div>
  );
}

// ── Chips and status ────────────────────────────────────────────────────────────
const chipForms: Record<ChipForm, string> = {
  outlined: 'border border-accent text-accent',
  filled: 'bg-primary text-primary-fg',
  mint: 'bg-bg-2 text-fg-2',
  warning: 'bg-warning-tint text-warning',
  muted: 'border border-line text-muted',
};
export function Chip({ form = 'outlined', children, className }: { form?: ChipForm; children: ReactNode; className?: string }) {
  return <span className={cx('inline-flex items-center gap-1.5 h-7 px-2.5 rounded-sm text-[13px] font-medium tracking-[0.04em] whitespace-nowrap', chipForms[form], className)}>{children}</span>;
}
export function StatusChip({ label }: { label: StatusLabel }) {
  const { pick } = useI18n();
  return <Chip form={label.chip}>{pick(label)}</Chip>;
}

// ── Notices ─────────────────────────────────────────────────────────────────────
type Tone = 'info' | 'warning' | 'error' | 'success';
const tones: Record<Tone, string> = {
  info: 'bg-bg-2 border-line-2 border-s-fg text-fg-2',
  warning: 'bg-warning-tint border-line-2 border-s-warning text-fg',
  error: 'bg-error-tint border-line-2 border-s-error text-fg',
  success: 'bg-bg-2 border-accent/40 border-s-accent text-fg-2',
};
export function Notice({ tone = 'info', title, children, className }: { tone?: Tone; title?: string; children?: ReactNode; className?: string }) {
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={cx('border border-s-[3px] rounded-sm px-4 py-3.5 text-[15px] leading-relaxed', tones[tone], className)}>
      {title ? <p className="font-medium text-fg">{title}</p> : null}
      {children ? <div className={cx(title && 'mt-1 text-sm')}>{children}</div> : null}
    </div>
  );
}

// ── Surfaces ────────────────────────────────────────────────────────────────────
export function Card({ children, className, lift }: { children: ReactNode; className?: string; lift?: boolean }) {
  return <section className={cx('rounded-sm border p-5', lift ? 'bg-surface border-line-2' : 'bg-transparent border-line', className)}>{children}</section>;
}
export function Stat({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: string; tone?: 'warning' }) {
  return (
    <div className="rounded-sm border border-line px-5 py-4">
      <p className="label">{label}</p>
      <p className={cx('mt-1 text-[28px] font-light leading-tight num', tone === 'warning' && 'text-warning')}>{value}</p>
      {hint ? <p className="text-[13px] text-muted mt-1">{hint}</p> : null}
    </div>
  );
}
export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-sm border border-dashed border-line-2 px-6 py-10 text-center">
      <p className="text-[15px] text-fg-2">{title}</p>
      {children ? <div className="mt-4 flex justify-center">{children}</div> : null}
    </div>
  );
}
export function Skeleton({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cx('flex flex-col gap-3', className)} aria-busy>
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="skeleton h-5" style={{ width: `${100 - (i % 3) * 18}%` }} />
      ))}
    </div>
  );
}

// ── Tables ──────────────────────────────────────────────────────────────────────
/**
 * Cell padding is the one part of a table that costs width without carrying content: eight columns of
 * `px-4` are 256 px of the row. Below 1360 px — the same breakpoint the queue splits at — they go to
 * `px-3`, which is what keeps the widest tables (payments in English) inside their card at 1280
 * instead of scrolling. At 1440 nothing changes.
 */
export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('overflow-x-auto rounded-sm border border-line', className)}>
      <table className="w-full text-sm border-collapse [&_th]:label [&_th]:text-start [&_th]:font-medium [&_th]:px-3 min-[1360px]:[&_th]:px-4 [&_th]:py-3 [&_th]:border-b [&_th]:border-line [&_td]:px-3 min-[1360px]:[&_td]:px-4 [&_td]:py-3 [&_td]:border-b [&_td]:border-line [&_tr:last-child_td]:border-b-0 [&_tbody_tr:hover]:bg-bg-2/60">
        {children}
      </table>
    </div>
  );
}

/**
 * A row that scrolls sideways on a phone starts at its first item, so the one that matters
 * (the current step, the selected tab) can sit off-screen with nothing to say so. Bring it
 * into the middle of the row — physical `scrollBy`, so it works in RTL and LTR alike, and
 * only when the row really overflows, so the page itself never jumps.
 */
function useScrollActiveIntoView<T extends HTMLElement>(deps: unknown[]) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const box = ref.current;
    const el = box?.querySelector<HTMLElement>('[data-active="true"]');
    if (!box || !el || box.scrollWidth <= box.clientWidth + 1) return;
    const b = box.getBoundingClientRect();
    const e = el.getBoundingClientRect();
    const delta = e.left + e.width / 2 - (b.left + b.width / 2);
    if (Math.abs(delta) > 1) box.scrollBy({ left: delta });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
}

// ── Stepper (horizontal) and timeline (vertical) — the two request-tracking primitives ──
export function Stepper({ steps, current, cancelled }: { steps: { key: string; label: string }[]; current: number; cancelled?: boolean }) {
  const ref = useScrollActiveIntoView<HTMLOListElement>([current, cancelled, steps.length]);
  return (
    <ol ref={ref} className="flex items-start gap-0 overflow-x-auto py-1" aria-label="progress">
      {steps.map((s, i) => {
        // Every label here is a milestone in the past tense — "Fee set", "Report ready" — so the step
        // the request has *reached* is a thing that has happened and is drawn filled. It used to be
        // drawn as a hollow ring, which reads as "still to do": a valuation whose report was uploaded
        // showed «التقرير جاهز» as the outstanding step, directly above a card saying it was uploaded.
        const reached = i <= current;
        const active = i === current && !cancelled;
        return (
          <li key={s.key} data-active={active || (cancelled && i === current) ? 'true' : undefined} className="flex-1 min-w-20 flex flex-col items-center gap-2 relative">
            {i > 0 ? <span className={cx('absolute top-2 end-1/2 w-full h-px', reached ? 'bg-accent' : 'bg-line-2')} aria-hidden /> : null}
            <span className={cx('relative z-10 size-4 rounded-full border-2', cancelled ? 'border-line-2 bg-bg' : reached ? 'bg-accent border-accent' : 'border-line-2 bg-bg')} />
            <span className={cx('text-[12px] text-center leading-tight', active ? 'text-fg font-medium' : reached ? 'text-fg-2' : 'text-muted')}>{s.label}</span>
          </li>
        );
      })}
    </ol>
  );
}
export function Timeline({ items }: { items: { id: string; title: ReactNode; body?: ReactNode; at: string | Date; who?: string | null; tone?: 'warning' | 'muted' }[] }) {
  if (!items.length) return null;
  return (
    <ol className="relative border-s border-line ms-2 ps-6 flex flex-col gap-6">
      {items.map((it) => (
        <li key={it.id} className="relative">
          <span className={cx('absolute -start-[29px] top-1.5 size-2.5 rounded-full', it.tone === 'warning' ? 'bg-warning' : it.tone === 'muted' ? 'bg-line-2' : 'bg-accent')} aria-hidden />
          <p className="text-[13px] text-muted">
            <DateText at={it.at} />
            {it.who ? <span> · {it.who}</span> : null}
          </p>
          <p className="font-medium text-fg mt-0.5">{it.title}</p>
          {it.body ? <div className="text-sm text-fg-2 mt-1">{it.body}</div> : null}
        </li>
      ))}
    </ol>
  );
}

// ── Countdown — the one live element in the panel ───────────────────────────────
export function Countdown({ deadline, expiredLabel }: { deadline: string | Date; expiredLabel: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const left = Math.max(0, Math.floor((new Date(deadline).getTime() - now) / 1000));
  if (left === 0) return <span className="text-warning font-medium">{expiredLabel}</span>;
  const mm = String(Math.floor(left / 60)).padStart(2, '0');
  const ss = String(left % 60).padStart(2, '0');
  return <span className={cx('num font-medium', left < 600 && 'text-warning')}>{mm}:{ss}</span>;
}

// ── Dialog (native) ─────────────────────────────────────────────────────────────
export function Dialog({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} onClose={onClose} className="m-auto w-[min(92vw,520px)] rounded-sm border border-line-2 bg-surface text-fg p-0 backdrop:bg-ink/60">
      <div className="p-6">
        <h2 className="mb-4">{title}</h2>
        {children}
      </div>
    </dialog>
  );
}

// ── Logo ────────────────────────────────────────────────────────────────────────
export function Logo({ variant = 'green', height = 40, className }: { variant?: 'green' | 'mint'; height?: number; className?: string }) {
  return <img src={variant === 'mint' ? '/brand/wasity-logo-horizontal-mint.svg' : '/brand/wasity-logo-horizontal.svg'} alt="وسيطي Wasity" style={{ height }} className={className} draggable={false} />;
}

// ── Page furniture ──────────────────────────────────────────────────────────────

export function PageHeader({ crumbs, title, meta, chip, actions, children }: { crumbs?: { to?: string; label: string }[]; title: ReactNode; meta?: ReactNode; chip?: ReactNode; actions?: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
      <div className="min-w-0">
        {crumbs?.length ? (
          <nav className="flex gap-2 text-[13px] text-muted mb-2" aria-label="breadcrumb">
            {crumbs.map((c, i) => (
              <span key={i} className="flex gap-2">
                {c.to ? <Link to={c.to}>{c.label}</Link> : <span className={i === crumbs.length - 1 ? 'num' : ''}>{c.label}</span>}
                {i < crumbs.length - 1 ? <span aria-hidden>›</span> : null}
              </span>
            ))}
          </nav>
        ) : null}
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="min-w-0">{title}</h1>
          {chip}
        </div>
        {meta ? <p className="text-muted mt-1.5 text-[14px]">{meta}</p> : null}
        {children}
      </div>
      {actions ? <div className="flex flex-wrap gap-2.5">{actions}</div> : null}
    </div>
  );
}

/**
 * A row of tabs that behaves like one: a single tab stop (the selected tab), arrow keys move along the
 * row and select as they go — mirrored in Arabic, where the next tab is to the left — and Home/End
 * jump to the ends. With an `id`, each tab names the `TabPanel` of the same id, which says which tab
 * labels it. The roles were there before; the behaviour they promise a screen reader was not.
 */
export function Tabs<T extends string>({ id, items, value, onChange }: { id?: string; items: { key: T; label: string; count?: number }[]; value: T; onChange: (k: T) => void }) {
  const ref = useScrollActiveIntoView<HTMLDivElement>([value, items.length]);
  const { dir } = useI18n();
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const keys = items.map((it) => it.key);
    const i = Math.max(keys.indexOf(value), 0);
    const forward = dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    const back = dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
    const to = e.key === forward ? keys[(i + 1) % keys.length] : e.key === back ? keys[(i - 1 + keys.length) % keys.length] : e.key === 'Home' ? keys[0] : e.key === 'End' ? keys[keys.length - 1] : undefined;
    if (to === undefined) return;
    e.preventDefault();
    onChange(to);
    ref.current?.querySelector<HTMLButtonElement>(`[data-key="${to}"]`)?.focus();
  };
  return (
    <div ref={ref} className="flex gap-0 border-b border-line overflow-x-auto" role="tablist" onKeyDown={onKeyDown}>
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          role="tab"
          id={id ? `${id}-tab-${it.key}` : undefined}
          aria-controls={id ? `${id}-panel` : undefined}
          aria-selected={it.key === value}
          tabIndex={it.key === value ? 0 : -1}
          data-key={it.key}
          data-active={it.key === value ? 'true' : undefined}
          onClick={() => onChange(it.key)}
          className={cx('px-4 py-3 -mb-px border-b-2 text-[14px] font-medium whitespace-nowrap cursor-pointer', it.key === value ? 'border-accent text-fg' : 'border-transparent text-muted hover:text-fg')}
        >
          {it.label}
          {/* Only the digit is a number: an LTR isolate around « · 2» pushes the dot to the far end in Arabic. */}
          {it.count !== undefined ? <>{' · '}<span className="num">{it.count}</span></> : null}
        </button>
      ))}
    </div>
  );
}

/** What the selected tab of `Tabs` (same `id`) shows. One panel whose contents change, labelled by whichever tab is selected. */
export function TabPanel({ id, value, className, children }: { id: string; value: string; className?: string; children: ReactNode }) {
  return (
    <div role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-tab-${value}`} tabIndex={0} className={cx('focus-visible:outline-none', className)}>
      {children}
    </div>
  );
}

export function Toolbar({ children, end }: { children: ReactNode; end?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2.5">
      {children}
      {end ? <span className="ms-auto text-[13px] text-muted">{end}</span> : null}
    </div>
  );
}

/** Label/value pairs, the way the mocks lay out property and payment facts. */
export function KV({ items, className }: { items: { k: string; v: ReactNode; num?: boolean }[]; className?: string }) {
  return (
    <dl className={cx('grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-[14px]', className)}>
      {items.map((it, i) => (
        <div key={i} className="contents">
          <dt className="text-muted">{it.k}</dt>
          <dd className={cx('m-0 min-w-0 break-words', it.num && 'num')}>{it.v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Avatar({ name, size = 40 }: { name: string | null | undefined; size?: number }) {
  const ch = (name ?? '').trim().charAt(0) || '·';
  return (
    <span className="inline-flex items-center justify-center rounded-full bg-bg-2 text-fg-2 font-medium flex-none" style={{ width: size, height: size, fontSize: size > 44 ? 20 : 15 }} aria-hidden>
      {ch}
    </span>
  );
}

export function Bar({ value, max, warn, className }: { value: number; max: number; warn?: boolean; className?: string }) {
  const p = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className={cx('h-1.5 bg-line rounded-sm overflow-hidden', className)} role="progressbar" aria-valuenow={value} aria-valuemax={max}>
      <div className={cx('h-full', warn || (max > 0 && value >= max) ? 'bg-warning' : 'bg-accent')} style={{ width: `${p}%` }} />
    </div>
  );
}

export function Pagination({ offset, limit, count, onChange, labels }: { offset: number; limit: number; count: number; onChange: (offset: number) => void; labels: { prev: string; next: string; page: string } }) {
  const page = Math.floor(offset / limit) + 1;
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[13px] text-muted num">
        {labels.page} {page}
      </span>
      <div className="flex gap-2">
        <Button variant="secondary" className="min-h-9 px-3 text-[14px]" disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - limit))}>
          {labels.prev}
        </Button>
        <Button variant="secondary" className="min-h-9 px-3 text-[14px]" disabled={count < limit} onClick={() => onChange(offset + limit)}>
          {labels.next}
        </Button>
      </div>
    </div>
  );
}

/** A confirmation with an optional reason field — the one shape every destructive action uses. */
export function ConfirmDialog({ open, onClose, title, body, confirmLabel, cancelLabel, danger, reason, onConfirm, loading }: { open: boolean; onClose: () => void; title: string; body?: ReactNode; confirmLabel: string; cancelLabel: string; danger?: boolean; reason?: { label: string; required?: boolean; placeholder?: string }; onConfirm: (reason: string) => unknown; loading?: boolean }) {
  const [text, setText] = useState('');
  useEffect(() => {
    if (!open) setText('');
  }, [open]);
  return (
    <Dialog open={open} onClose={onClose} title={title}>
      {body ? <div className="text-[15px] text-fg-2 mb-4">{body}</div> : null}
      {reason ? (
        <Field label={reason.label} htmlFor="confirm-reason">
          <Textarea id="confirm-reason" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={reason.placeholder} />
        </Field>
      ) : null}
      <div className="flex gap-2 justify-end mt-5">
        <Button variant="secondary" onClick={onClose}>
          {cancelLabel}
        </Button>
        <Button variant={danger ? 'danger' : 'primary'} loading={loading} disabled={!!reason?.required && !text.trim()} onClick={() => void onConfirm(text.trim())}>
          {confirmLabel}
        </Button>
      </div>
    </Dialog>
  );
}

/** Tiny SVG trend line for dashboards; one series per path, scaled to the tallest point. */
export function Sparkline({ series, height = 56, labels }: { series: { points: number[]; color?: string; area?: boolean }[]; height?: number; labels?: { start: string; end: string } }) {
  const w = 320;
  const max = Math.max(1, ...series.flatMap((s) => s.points));
  const path = (pts: number[]) => pts.map((v, i) => `${i === 0 ? 'M' : 'L'}${((i / Math.max(1, pts.length - 1)) * w).toFixed(1)},${(height - 4 - (v / max) * (height - 8)).toFixed(1)}`).join(' ');
  return (
    <div>
      <svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" className="w-full" style={{ height }} role="img" aria-hidden>
        {series.map((s, i) => (
          <g key={i}>
            {s.area && s.points.length > 1 ? <path d={`${path(s.points)} L${w},${height} L0,${height} Z`} fill="var(--accent)" opacity="0.1" /> : null}
            {s.points.length > 1 ? <path d={path(s.points)} fill="none" stroke={s.color ?? 'var(--accent)'} strokeWidth="2" /> : null}
          </g>
        ))}
      </svg>
      {labels ? (
        <div className="flex justify-between text-[13px] text-muted mt-1">
          <span>{labels.start}</span>
          <span>{labels.end}</span>
        </div>
      ) : null}
    </div>
  );
}

/** Simple vertical bars with value labels, for "by status" charts. */
export function BarChart({ data }: { data: { label: string; value: number; tone?: 'accent' | 'ink' | 'muted' }[] }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="grid gap-2 items-end" style={{ gridTemplateColumns: `repeat(${data.length}, minmax(0, 1fr))`, height: 200 }}>
      {data.map((d, i) => (
        <div key={i} className="flex flex-col items-center justify-end gap-1 h-full min-w-0">
          <span className="text-[12px] num">{d.value}</span>
          <div className={cx('w-full max-w-10 rounded-sm', d.tone === 'ink' ? 'bg-primary' : d.tone === 'muted' ? 'bg-line-2' : 'bg-accent')} style={{ height: `${Math.max(4, (d.value / max) * 150)}px` }} />
          <span className="text-[12px] text-muted text-center leading-tight line-clamp-2">{d.label}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Why a query or a save failed. A code the product words (see `lib/errors.ts` and `errors.*` in the
 * dictionaries) gets its own sentence; anything else gets the caller's generic line. The raw code is
 * shown only in a dev build — an English token inside an Arabic notice tells the reader nothing.
 */
export function ErrorNotice({ error, retry, labels }: { error: unknown; retry?: () => void; labels: { message: string; retry: string } }) {
  const { t } = useI18n();
  const known = specificErrorText(error, t);
  const code = errorCodeOf(error);
  return (
    <Notice tone="error">
      <span>{known ?? labels.message}</span>
      {!known && import.meta.env.DEV && code ? <span className="num text-[13px] ms-2">({code})</span> : null}
      {retry ? (
        <Button variant="text" className="ms-3" onClick={retry}>
          {labels.retry}
        </Button>
      ) : null}
    </Notice>
  );
}
